import { DateMode, MatrixColumnType, type FormField, FormFieldChoice,
  type MatrixColumn, type MatrixTableRow, type MatrixValue, type MatrixRow } from '@/models';
import { getLocalTimezoneDetails, getOnlyTimeSeconds, getTimeFromSeconds, getUTCJSDate, getUTCSeconds } from '@/utils';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';

export const getInitialMatrixData = (localField: FormField) => {
  const matrixRows: MatrixTableRow[] = [];

  for (const row of localField.options.rows!) {
    const rowConfig: MatrixTableRow = {
      rowId: `${row.id}`,
      rowHeader: row.label,
      dynamicRowConfig: row.dynamicRowConfig,

      columnValues: cloneDeep(localField.options.columns)
    };

    rowConfig.columnValues!.forEach(column => {
      assignColumnValues(rowConfig, row, column, localField.matrixValues!);
    });

    matrixRows.push(rowConfig);

    if (rowConfig.dynamicRows) {
      rowConfig.dynamicRows.forEach(dynamicRowId => {
        let dynamicRow;
        for (const data of localField.matrixValues!) {
          if (data.rowId == dynamicRowId) {
            dynamicRow = data;
            break;
          }
        }

        if (dynamicRow) {
          const subRow: MatrixTableRow = {
            rowId: dynamicRow.rowId,
            rowHeader: rowConfig.rowHeader,
            enableClear: true,

            columnValues: cloneDeep((localField.options.columns))
          };

          subRow.columnValues!.forEach(column => {
            dynamicRow.id = dynamicRow.rowId;
            assignColumnValues(subRow, dynamicRow, column, localField.matrixValues!);
          });
          matrixRows.push(subRow);
        }
      });
    }
  }

  return matrixRows;
};

const assignColumnValues = (rowConfig: MatrixTableRow, row: MatrixRow, column: MatrixColumn, matrixValues: MatrixValue[]) => {
  for (const rowData of matrixValues) {
    if (rowData.rowId == `${row.id}`) {
      rowConfig.dynamicRows = rowData.dynamicRows;

      for (const columnData of rowData.values) {
        if (columnData.id == `${column.id}` ) {
          column.conditions = columnData.conditions;
          if (columnData.value != '') {
            switch (column.type) {
              case MatrixColumnType.TEXT:
                column.textValue = columnData.value as string;
                break;

              case MatrixColumnType.NUMBER:
                if (column.options?.range) {
                  column.textValue = columnData.value as string;
                } else {
                  column.numberValue = columnData.value ? parseFloat(columnData.value as string) : null;
                }
                break;

              case MatrixColumnType.SINGLE_CHOICE:
              case MatrixColumnType.MULTIPLE_CHOICE:
                column.booleanValue = columnData.value === 'true';
                break;
            
              case MatrixColumnType.DATE:
                if (!columnData.value) {
                  break;
                }
                if (column.options!.mode === DateMode.DATE_TIME) {
                  if (column.options!.absoluteDate) {
                    // when absolute date is selected, it means the time is recorded in UTC seconds
                    column.dateValue = getUTCJSDate(parseInt(columnData.value as string, 10));
                  } else {
                    column.dateValue = DateTime.fromSeconds(parseInt(columnData.value as string)).toJSDate();
                  }
                  if (columnData.timezoneRecorded) {
                    column.timezoneRecorded = typeof columnData.timezoneRecorded === 'string' ?
                      JSON.parse(columnData.timezoneRecorded) :
                      columnData.timezoneRecorded;
                  } else {
                    column.timezoneRecorded = getLocalTimezoneDetails();
                  }
                } else if (column.options!.mode === DateMode.DATE) {
                  column.dateValue = getUTCJSDate(parseInt(columnData.value as string, 10));
                } else if (column.options!.mode === DateMode.TIME) {
                  column.dateValue = getTimeFromSeconds(parseInt(columnData.value as string, 10));
                }
                if (!column.options) {
                  column.options = {};
                }
                if (columnData.dateShifting) {
                  column.options!.dateShifting = columnData.dateShifting;
                }
                break;

              case MatrixColumnType.DROPDOWN:
                if (column.options?.multipleChoice) {
                  const values = (columnData.value as string).split(',');
                  const choices = [];
                  for (const choice of column.options!.choices!) {
                    for (const value of values) {
                      if (choice.value == value) {
                        choices.push(choice);
                        break;
                      }
                    }
                  }
                  column.choiceValue = choices;
                } else {
                  const choice = column.options!.choices!.find(choiceItem => choiceItem.value == columnData.value);
                  if (choice) {
                    column.choiceValue = choice;
                  }
                }
                break;

              case MatrixColumnType.FILE:
                if (columnData.value) {
                  column.fileIds = JSON.parse(columnData.value as string);
                }
                break;
              
              default:
                column.textValue = columnData.value as string;
                break;
            }
          }
          
          break;
        }
      }
      break;
    }
  }
};

export const matrixDataCalculations = (columns: MatrixColumn[], matrixData: MatrixTableRow[]) => {
  columns.forEach(column => {
    if (column.dynamicRowColumnFormula) {
      const columnId = column.dynamicRowColumnFormula.columnId;
      const operator = column.dynamicRowColumnFormula.operator;
  
      for (const matrixTableRow of matrixData) {
        if (
          typeof matrixTableRow.rowId == 'string' &&
            matrixTableRow.rowId.includes('-')
        ) {
          // this means it's a dynamic row
          continue;
        }
        const columnValues: number[] = [];
        const value = matrixTableRow.columnValues!.find(col => col.id == columnId)?.numberValue;
        if (value) {
          columnValues.push(value);
        }
  
        let addValueRow: MatrixTableRow = matrixTableRow;
        if (matrixTableRow.dynamicRows) {
          matrixTableRow.dynamicRows.forEach(dynamicRowId => {
            const dynamicRow = matrixData.find(
              row => row.rowId == dynamicRowId
            );
            if (dynamicRow) {
              const dynamicRowValue = dynamicRow.columnValues!.find(col => col.id == columnId)?.numberValue;
              if (dynamicRowValue) {
                columnValues.push(dynamicRowValue);
              }
            }
          });
          addValueRow.columnValues!.find(col => col.id == column.id)!.numberValue = null;
          matrixTableRow.dynamicRows.forEach(dynamicRowId => {
            for (const data of matrixData) {
              if (data.rowId == dynamicRowId) {
                data.columnValues!.find(col => col.id == column.id)!.numberValue = null;
              }
            }
          });
          const lastlDynamicRowId =
              matrixTableRow.dynamicRows[matrixTableRow.dynamicRows.length - 1];
          addValueRow = matrixData.find(
            row => row.rowId == lastlDynamicRowId
          )!;
        }
  
        let finalFormulaValue = 0;
        if (operator === '+') {
          let total = 0;
          for (const i of columnValues) {
            total += i;
          }
          finalFormulaValue = total;
        }
  
        if (finalFormulaValue > 0) {
          addValueRow.columnValues!.find(col => col.id == column.id)!.numberValue = finalFormulaValue;
        }
      }
    }
  
    if (column.columnFormula) {
      const columnId = column.columnFormula.columnId;
      const destColumnId = column.columnFormula.destColumnId;
      const rowId = column.columnFormula.rowId;
      const operator = column.columnFormula.operator;
      const columnValues: number[] = [];
      for (const matrixTableRow of matrixData) {
        if (matrixTableRow.rowId == `${rowId}` && columnId == destColumnId) {
          continue;
        }
        const value = matrixTableRow.columnValues!.find(col => col.id == columnId)?.numberValue;
        if (value) {
          columnValues.push(value);
        }
      }
  
      let finalFormulaValue = 0;
      if (operator === '+') {
        let total = 0;
        for (const i of columnValues) {
          total += i;
        }
        finalFormulaValue = total;
      }
  
      const addValueRow = matrixData.find(rowData => rowData.rowId == `${rowId}`);
      if (addValueRow) {
        addValueRow.columnValues!.find(col => col.id == destColumnId)!.numberValue = finalFormulaValue;
      }
      
    }
  });
  return matrixData;
};

export const getMatrixValues = (matrixData: MatrixTableRow[]) => {
  const matrixValues = [];

  for (const tableRow of matrixData) {
    const columnValues = [];

    for (const column of tableRow.columnValues!) {
      const value = getColumnValue(column);

      if (isColumnValue(value)) {
        columnValues.push({
          id: `${column.id}`,
          ...(value as ColumnValue)
        });
      } else {
        columnValues.push({
          id: `${column.id}`,
          value: value ? `${value}` : ''
        });
      }
    }

    let dynamicRows: string[] | undefined;
    if (tableRow.dynamicRows && tableRow.dynamicRows.length > 0) {
      dynamicRows = tableRow.dynamicRows;
    }

    matrixValues.push({
      rowId: tableRow.rowId,
      values: columnValues,
      dynamicRows
    });
  }

  let hasValue = false;

  for (const matrixValue of matrixValues) {
    for (const value of matrixValue.values) {
      if (value.value) {
        hasValue = true;
      }
    }
  }

  return hasValue ? matrixValues : undefined;
};

type ColumnValue = { value: string; timezoneRecorded: { timezone: string }}

function isColumnValue(value: unknown): value is ColumnValue {
  return (
    value !== null &&
    typeof value === 'object' &&
    'value' in value
  );
}

const getColumnValue = (column: MatrixColumn) => {
  let value : string | number | ColumnValue = column.textValue ? column.textValue : '';

  switch (column.type) {
    case MatrixColumnType.NUMBER:
      if (!column.options?.range) {
        value = column.numberValue ? `${column.numberValue}` : '';
      }
      break;
  
    case MatrixColumnType.DATE:
      if (column.dateValue && typeof column.dateValue === 'object') {
        if (column.options!.mode === DateMode.DATE) {
          value = getUTCSeconds(column.dateValue);
        } else if (column.options!.mode === DateMode.DATE_TIME) {
          if (column.options!.absoluteDate) {
            value = getUTCSeconds(column.dateValue);
            return {
              value,
              timezoneRecorded: column.timezoneRecorded
            };
          } else {
            return {
              value: column.dateValue.getTime() / 1000,
              timezoneRecorded: column.timezoneRecorded
            };
          }
        } else {
          value = getOnlyTimeSeconds(column.dateValue);
        }
      }
      break;

    case MatrixColumnType.SINGLE_CHOICE:
    case MatrixColumnType.MULTIPLE_CHOICE:
      value = column.booleanValue ? `${column.booleanValue}` : '';
      break;

    case MatrixColumnType.DROPDOWN:
      if (column.options!.multipleChoice) {
        const selectedChoices: string[] = [];
        (column.choiceValue as FormFieldChoice[])?.forEach(choice => {
          selectedChoices.push(`${choice.value}`);
        });
  
        value = selectedChoices.join(',');
      } else {
        value = (column.choiceValue as FormFieldChoice)?.value;
      }
      break;

    default:
      break;
  }
  return value;
};

export const checkIfMatrixHasValidValues = (field: FormField, matrixRows: MatrixTableRow[]) => {
  const allRadio = field.options.columns!.every(
    column =>
      column.type === MatrixColumnType.SINGLE_CHOICE ||
      column.type === MatrixColumnType.MULTIPLE_CHOICE
  );
  let validValue = false;
  if (allRadio) {
    // check if all rows have at least one value entered
    const matrixValue = getMatrixValues(matrixRows);
    const allRowsAnswered =
      matrixValue?.every(
        row => row.values.some(value => value.value !== undefined && value.value !== null && value.value !== '')
      );
    validValue = allRowsAnswered === true;
  } else {
    //just check if at least one value is entered in the matrix
    const matrixValue = getMatrixValues(matrixRows);
    const hasValue =
      matrixValue?.filter(row => row.values.some(value => value.value !== undefined && value.value !== null && value.value !== ''));
    validValue = (hasValue && hasValue.length > 0) ? true : false;
  }
  return validValue;
};
